import type { BOMActivity, BOMItem } from "../api-models";

// A BOM version's activity tree, in the shapes the screens that scope work to it need.
// The server sends the tree as nested roots (loadActivityTree, bom_activity.go); every
// lookup here is built from that one payload rather than re-fetched per node.

/** Every node of the tree by id, roots and descendants alike. */
export function flattenActivities(
  nodes: BOMActivity[] = [],
  into = new Map<number, BOMActivity>(),
): Map<number, BOMActivity> {
  for (const n of nodes) {
    into.set(n.id, n);
    flattenActivities(n.children ?? [], into);
  }
  return into;
}

// "1.2 - Excavation". The WBS code is what site and QS staff quote to each other, and
// activity names repeat across branches, so the code leads wherever a single activity is
// named. Codes are free text and may be blank on an imported BOM - fall back to the name
// rather than rendering a dangling separator.
export function activityLabel(activity?: {
  code?: string;
  name?: string;
}): string {
  if (!activity) return "";
  const { code, name } = activity;
  if (!code) return name ?? "";
  return name ? `${code} - ${name}` : code;
}

/** "1 - Substructure > 1.2 - Excavation" - an activity's place in the work breakdown. */
export function activityPath(
  activityId: number | undefined,
  byId: Map<number, BOMActivity>,
): string {
  const segments: string[] = [];
  let node = activityId == null ? undefined : byId.get(activityId);
  while (node) {
    segments.unshift(activityLabel(node));
    node = node.parentId ? byId.get(node.parentId) : undefined;
  }
  return segments.join(" > ") || "Unassigned";
}

/** The activity path of the piece of work a BOM item hangs off. */
export function bomItemActivityPath(
  item: BOMItem,
  byId: Map<number, BOMActivity>,
): string {
  return activityPath(item.activityId, byId);
}

// coveredActivityIds mirrors expandActivitySelection (bom_activity.go): a selected
// activity covers itself and everything beneath it, so picking a top-level activity picks
// up the whole branch's materials without the user naming each child. Deriving it here as
// well as on the server is deliberate - it is what lets the item picker and the "add all"
// button show exactly what the server will accept, rather than failing at save.
export function coveredActivityIds(
  selected: number[],
  byId: Map<number, BOMActivity>,
): Set<number> {
  const covered = new Set<number>();
  const queue = selected.filter((id) => byId.has(id));
  for (const id of queue) covered.add(id);
  for (let i = 0; i < queue.length; i++) {
    for (const child of byId.get(queue[i])?.children ?? []) {
      if (!covered.has(child.id)) {
        covered.add(child.id);
        queue.push(child.id);
      }
    }
  }
  return covered;
}

/** The BOM items belonging to the selected work, in the tree's own display order. */
export function bomItemsInScope(
  items: BOMItem[],
  selected: number[],
  byId: Map<number, BOMActivity>,
): BOMItem[] {
  if (selected.length === 0) return [];
  const covered = coveredActivityIds(selected, byId);
  return items.filter((it) => covered.has(it.activityId));
}

// The ledger lives on the BOM item itself, so a row's remaining request balance is its own
// arithmetic - this mirrors BOMItem.AvailableToRequest() in bom.go.
export function bomItemBalance(l: BOMItem): number {
  return l.approvedQty + l.variationQty - l.reductionQty - l.committedMrqQty;
}
